<template>
    <div class="position-relative">
        <transition name="fade-left">
            <span
                v-if="scrolling && leftArrow"
                @click="scrollLeft($refs.wrapper, $event)"
                @mouseenter="temporarilyDisableDragWhileHoveringArrow"
                @mouseleave="restoreDragAfterArrowClick"
                class="scrollable__arrow scrollable__arrow-left"
                data-scroll-arrow="left"
                role="button"
                aria-label="Scroll left"
                tabindex="0"
                @keydown.enter="scrollLeft($refs.wrapper, $event)"
                @keydown.space.prevent="scrollLeft($refs.wrapper, $event)"
                :style="leftArrowStyle"
            >
                <font-awesome-icon
                    :icon="['far', 'chevron-left']"
                >
                </font-awesome-icon>
            </span>
        </transition>
        <transition name="fade-right">
            <span
                v-if="scrolling && rightArrow"
                @click="scrollRight($refs.wrapper, $event)"
                @mouseenter="temporarilyDisableDragWhileHoveringArrow"
                @mouseleave="restoreDragAfterArrowClick"
                class="scrollable__arrow scrollable__arrow-right"
                data-scroll-arrow="right"
                role="button"
                aria-label="Scroll right"
                tabindex="0"
                @keydown.enter="scrollRight($refs.wrapper, $event)"
                @keydown.space.prevent="scrollRight($refs.wrapper, $event)"
                :style="rightArrowStyle"
            >
                <font-awesome-icon
                    :icon="['far', 'chevron-right']"
                >
                </font-awesome-icon>
            </span>
        </transition>
        <div
            class="overflow-auto hide-scrollbar"
            @scroll="onScroll"
            ref="wrapper"
        >
            <slot></slot>
        </div>
    </div>
</template>

<script>
import createDraggableScroll from './draggableScroll';

export default {
  name: 'ScrollableWidget',
  props: {
    arrowBgColor: {
      type: String,
      default: 'transparent',
    },
    arrowFgColor: {
      type: String,
    },
    refresh: {
      type: Boolean,
    },
  },
  data() {
    return {
      leftArrow: false,
      rightArrow: false,
      scrolling: false,
      leftArrowStyle: {
        background: `linear-gradient(to right, ${this.arrowBgColor} 0%, ${this.arrowBgColor} 70%, rgba(255,255,255,0) 100%)`,
        color: this.arrowFgColor || null,
      },
      rightArrowStyle: {
        background: `linear-gradient(to left, ${this.arrowBgColor} 0%, ${this.arrowBgColor} 70%, rgba(255,255,255,0) 100%)`,
        color: this.arrowFgColor || null,
      },
      cleanup: null,
      shouldPreventDragWhileHoveringArrow: false,
    };
  },
  methods: {
    onScroll(event) {
      this.handleScroll(event);
      this.$emit('scroll', event);
    },
    handleScroll({ target: { offsetWidth, scrollLeft, scrollWidth } }) {
      if (offsetWidth >= scrollWidth) {
        this.scrolling = false;
        return;
      }

      this.scrolling = true;

      const maxScrollLeft = scrollWidth - offsetWidth;

      this.leftArrow = scrollLeft > 0;
      this.rightArrow = scrollLeft < (maxScrollLeft - 1);
    },
    scrollRight(scrollContainer, clickEvent) {
      this.preventEventBubbling(clickEvent);
      this.scrollContainerHorizontally(scrollContainer, 500);
      this.restoreDragAfterArrowClick();
    },
    scrollLeft(scrollContainer, clickEvent) {
      this.preventEventBubbling(clickEvent);
      this.scrollContainerHorizontally(scrollContainer, -500);
      this.restoreDragAfterArrowClick();
    },
    preventEventBubbling(event) {
      if (event) {
        event.stopPropagation();
      }
    },
    scrollContainerHorizontally(container, scrollDistance) {
      container.scrollTo({
        left: container.scrollLeft + scrollDistance,
        behavior: 'smooth'
      });
    },
    temporarilyDisableDragWhileHoveringArrow() {
      this.shouldPreventDragWhileHoveringArrow = true;
    },
    restoreDragAfterArrowClick() {
      this.shouldPreventDragWhileHoveringArrow = false;
    },
  },
  watch: {
    refresh() {
      this.handleScroll({
        target: this.$refs.wrapper,
      });
    }
  },
  mounted() {
    const handleResize = () => {
      this.handleScroll({
        target: this.$refs.wrapper,
      });
    };

    window.addEventListener('resize', handleResize);
    this.$once('hook:destroyed', () => {
      window.removeEventListener('resize', handleResize);
    });

    handleResize();

    const { initDraggableScroll } = createDraggableScroll();
    this.cleanup = initDraggableScroll(this.$refs.wrapper, () => this.shouldPreventDragWhileHoveringArrow);
  },
  beforeDestroy() {
    if (this.cleanup) {
      this.cleanup();
    }
  }
};
</script>

<style lang="scss">
    .scrollable__arrow {
        position: absolute;
        top: 0;
        padding: 0 3%;
        z-index: 10;
        height: 100%;
        display: flex;
        flex-direction: column;
        cursor: pointer;

        svg {
            flex-grow: 1;
        }

        &.scrollable__arrow-left {
            left: 0;
        }

        &.scrollable__arrow-right {
            right: 0;
        }
    }

    .fade-right-enter-active,
    .fade-right-leave-active,
    .fade-left-enter-active,
    .fade-left-leave-active {
        transition: all .3s;
    }

    .fade-right-enter, .fade-right-leave-to {
        transform: translateX(10px);
        opacity: 0;
    }

    .fade-left-enter, .fade-left-leave-to {
        transform: translateX(-10px);
        opacity: 0;
    }

    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }

    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
      overflow-y: auto;
    }
</style>
